import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { api } from '../../lib/api';

// Searchable account picker. Replaces the bounded <select> dropdowns we had
// in UserForm / NewInvoiceDialog / etc. that only ever showed the first N
// accounts (typically 100–500) and missed everything else when the prod
// roster has 1,899 schools.
//
// Behaviour:
//   • Renders the currently-selected account's name (or a placeholder).
//   • Click opens a search panel with debounced live `?q=` search hits.
//   • Selection emits the account id (string) via onChange. Pass `value`
//     to control the picker; the component fetches the row for display.
//
// Server contract: /api/admin/accounts?q=<term>&limit=20 returns
// { accounts: [...] }. Lookup-by-id (when initial value is set) hits
// /api/admin/accounts/:id directly.

const SEARCH_DEBOUNCE_MS = 200;

export default function AccountPicker({
  value,
  onChange,
  required = false,
  disabled = false,
  placeholder = 'Search by name, slug, DBN, principal, or city…',
  // Collapsed-state label when nothing is selected. Filter bars pass
  // "All accounts"; forms keep the default prompts.
  emptyLabel = null,
  filterStage = null,
  ariaInvalid = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Resolve display label when the parent passes a value but no selected
  // record yet (e.g., editing an existing user). One-shot fetch.
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.id === value) return;
    let cancelled = false;
    api.get(`/api/admin/accounts/${value}`)
      .then((d) => { if (!cancelled) setSelected(d?.account || null); })
      .catch(() => { if (!cancelled) setSelected(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        let url = `/api/admin/accounts?limit=20`;
        if (term) url += `&q=${encodeURIComponent(term)}`;
        if (filterStage) url += `&lifecycle_stage=${encodeURIComponent(filterStage)}`;
        const res = await api.get(url);
        setResults(res?.accounts || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, open, filterStage]);

  // Click-outside dismissal.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Focus input on open.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const select = (account) => {
    setSelected(account);
    onChange?.(account.id);
    setOpen(false);
    setQuery('');
  };
  const clear = () => {
    setSelected(null);
    onChange?.('');
  };

  const triggerLabel = selected
    ? selected.name + (selected.slug ? ` · ${selected.slug}` : '')
    : (
      <span className="text-gray-500">
        {emptyLabel || (required ? 'Select an account…' : 'Choose an account (optional)')}
      </span>
    );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        // aria-invalid not supported on role=button (eslint jsx-a11y); the
        // visual red border is sufficient for sighted users + UserForm
        // renders an associated <p> error message that's announced.
        className={`w-full text-left px-3 py-2 rounded-lg bg-white/[0.04] border ${
          ariaInvalid ? 'border-red-500/50' : 'border-white/[0.08]'
        } text-sm text-white flex items-center justify-between gap-2 hover:border-white/[0.18] transition-colors disabled:opacity-50`}
      >
        <span className="truncate">{triggerLabel}</span>
        {selected && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); clear(); }}
            className="shrink-0 p-1 rounded text-gray-500 hover:text-white hover:bg-white/[0.06]"
            aria-label="Clear account"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 rounded-xl border border-white/[0.08] bg-[#0a0a0f] shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.05]">
            <Search className="w-3.5 h-3.5 text-gray-500" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="p-0.5 rounded text-gray-500 hover:text-white"
                aria-label="Clear search"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {loading && (
              <div className="px-3 py-2.5 text-[12px] text-gray-500">Searching…</div>
            )}
            {!loading && results.length === 0 && (
              <div className="px-3 py-2.5 text-[12px] text-gray-500">
                {query ? 'No accounts match.' : 'Start typing to search.'}
              </div>
            )}
            {!loading && results.map((a) => {
              const isActive = selected?.id === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => select(a)}
                  className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-white/[0.04] ${
                    isActive ? 'bg-[#B0FC38]/[0.08]' : ''
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-white truncate">{a.name}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {[a.slug, a.typeSchoolDbn, a.typeSchoolCity, a.lifecycleStage]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {isActive && <Check className="w-3.5 h-3.5 text-[#B0FC38] shrink-0 mt-0.5" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
